import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  useAdminTenants,
  useApproveTenant,
  useRejectTenant,
  useSuspendTenant,
  useReactivateTenant,
  AdminTenant,
} from '@/hooks/useAdminTenants'
import { useImpersonation } from '@/hooks/useImpersonation'
import { useAuthStore } from '@personic/shared'
import EmployeesTab from '@/components/admin/EmployeesTab'
import TenantUsersTab from '@/components/admin/TenantUsersTab'
import AuditLogTab from '@/components/admin/AuditLogTab'
import TenantFeaturesTab from '@/components/admin/TenantFeaturesTab'
import ModuleAccessManager from '@/components/admin/ModuleAccessManager'
import PimSettingsManager from '@/components/admin/PimSettingsManager'

type StatusFilter = 'all' | 'pending' | 'active' | 'suspended' | 'cancelled'
type AdminTab = 'tenants' | 'employees' | 'users' | 'audit' | 'features' | 'modules' | 'pim'

const BASE_TABS = [
  { key: 'tenants' as const, label: 'Tenants', icon: BuildingIcon },
  { key: 'employees' as const, label: 'Employees', icon: UserGroupIcon },
  { key: 'users' as const, label: 'Tenant Users', icon: UsersIcon },
  { key: 'audit' as const, label: 'Audit Log', icon: ClipboardIcon },
  { key: 'features' as const, label: 'Tenant Features', icon: CogIcon },
]

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState<AdminTab>('tenants')
  const { user } = useAuthStore()
  const isSuperuser = user?.role === 'superuser'

  const tabs = useMemo(() => {
    if (isSuperuser) {
      return [
        ...BASE_TABS,
        { key: 'modules' as const, label: 'Module Access', icon: ModulesIcon },
        { key: 'pim' as const, label: 'PIM Settings', icon: PimIcon },
      ]
    }
    return BASE_TABS
  }, [isSuperuser])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
        <p className="text-gray-600 mt-1">Manage tenants, users, and permissions</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-personic-600 text-personic-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon
                className={`-ml-0.5 mr-2 h-5 w-5 ${
                  activeTab === tab.key ? 'text-personic-500' : 'text-gray-400 group-hover:text-gray-500'
                }`}
              />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'tenants' && <TenantsTab />}
        {activeTab === 'employees' && <EmployeesTab />}
        {activeTab === 'users' && <TenantUsersTab />}
        {activeTab === 'audit' && <AuditLogTab />}
        {activeTab === 'features' && <TenantFeaturesTab />}
        {activeTab === 'modules' && <ModuleAccessManager />}
        {activeTab === 'pim' && <PimSettingsManager />}
      </div>
    </div>
  )
}

// =============================================================================
// TENANTS TAB (Original functionality)
// =============================================================================

function TenantsTab() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [search, setSearch] = useState('')
  const [actionInProgress, setActionInProgress] = useState<string | null>(null)

  const navigate = useNavigate()
  const { impersonate, isStarting } = useImpersonation()

  const { data, isLoading, error } = useAdminTenants({
    status: statusFilter === 'all' ? undefined : statusFilter,
    search: search || undefined,
  })

  const approveMutation = useApproveTenant()
  const rejectMutation = useRejectTenant()
  const suspendMutation = useSuspendTenant()
  const reactivateMutation = useReactivateTenant()

  const handleAction = async (
    action: 'approve' | 'reject' | 'suspend' | 'reactivate',
    tenant: AdminTenant
  ) => {
    const confirmMessages: Record<typeof action, string> = {
      approve: `Approve "${tenant.name}"? They will gain full access to the platform.`,
      reject: `Reject "${tenant.name}"? Their application will be cancelled.`,
      suspend: `Suspend "${tenant.name}"? They will lose access to the platform.`,
      reactivate: `Reactivate "${tenant.name}"? They will regain access to the platform.`,
    }

    if (!confirm(confirmMessages[action])) return

    setActionInProgress(tenant.id)

    try {
      switch (action) {
        case 'approve':
          await approveMutation.mutateAsync(tenant.id)
          break
        case 'reject':
          await rejectMutation.mutateAsync(tenant.id)
          break
        case 'suspend':
          await suspendMutation.mutateAsync(tenant.id)
          break
        case 'reactivate':
          await reactivateMutation.mutateAsync(tenant.id)
          break
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || `Failed to ${action} tenant`)
    } finally {
      setActionInProgress(null)
    }
  }

  const handleImpersonate = async (tenantId: string) => {
    try {
      await impersonate(tenantId)
      navigate('/tenant/dashboard')
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start impersonation')
    }
  }

  const statusTabs: { key: StatusFilter; label: string; count?: number }[] = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'active', label: 'Active' },
    { key: 'suspended', label: 'Suspended' },
    { key: 'cancelled', label: 'Cancelled' },
  ]

  // Count tenants by status (if we have data)
  const statusCounts = data?.tenants.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1
    return acc
  }, {} as Record<string, number>) || {}

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === tab.key
                  ? 'bg-personic-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
              {tab.key !== 'all' && statusCounts[tab.key] !== undefined && (
                <span className="ml-2 text-xs opacity-75">({statusCounts[tab.key] || 0})</span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="w-full sm:w-64">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input w-full"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="card p-12 text-center text-gray-500">
          Loading tenants...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="card p-6 bg-red-50 border-red-200">
          <p className="text-red-700">Failed to load tenants. Please try again.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && data?.tenants.length === 0 && (
        <div className="card p-12 text-center">
          <p className="text-gray-600">
            {statusFilter === 'pending'
              ? 'No pending applications to review'
              : `No ${statusFilter === 'all' ? '' : statusFilter} tenants found`}
          </p>
        </div>
      )}

      {/* Tenant Table */}
      {!isLoading && !error && data && data.tenants.length > 0 && (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.tenants.map((tenant) => (
                <TenantRow
                  key={tenant.id}
                  tenant={tenant}
                  isLoading={actionInProgress === tenant.id}
                  isImpersonating={isStarting}
                  onAction={(action) => handleAction(action, tenant)}
                  onImpersonate={() => handleImpersonate(tenant.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Results Info */}
      {data && (
        <p className="text-sm text-gray-500">
          Showing {data.tenants.length} of {data.total} tenants
        </p>
      )}
    </div>
  )
}

function TenantRow({
  tenant,
  isLoading,
  isImpersonating,
  onAction,
  onImpersonate,
}: {
  tenant: AdminTenant
  isLoading: boolean
  isImpersonating: boolean
  onAction: (action: 'approve' | 'reject' | 'suspend' | 'reactivate') => void
  onImpersonate: () => void
}) {
  return (
    <tr className={isLoading ? 'opacity-50' : ''}>
      <td className="px-6 py-4 whitespace-nowrap">
        <Link to={`/employee/tenants/${tenant.id}`} className="hover:text-personic-600">
          <div>
            <div className="font-medium text-gray-900 hover:text-personic-600">{tenant.name}</div>
            <div className="text-sm text-gray-500">{tenant.slug}</div>
          </div>
        </Link>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm text-gray-900">{tenant.admin_name || '-'}</div>
          <div className="text-sm text-gray-500">{tenant.admin_email || '-'}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <StatusBadge status={tenant.status} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(tenant.created_at)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {tenant.last_login ? formatDate(tenant.last_login) : 'Never'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex justify-end gap-2">
          {tenant.status === 'active' && (
            <button
              onClick={onImpersonate}
              disabled={isLoading || isImpersonating}
              className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 disabled:opacity-50"
              title="Impersonate this tenant"
            >
              Impersonate
            </button>
          )}
          <TenantActions
            status={tenant.status}
            disabled={isLoading}
            onAction={onAction}
          />
        </div>
      </td>
    </tr>
  )
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
  }

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
        styles[status] || styles.active
      }`}
    >
      {status}
    </span>
  )
}

function TenantActions({
  status,
  disabled,
  onAction,
}: {
  status: string
  disabled: boolean
  onAction: (action: 'approve' | 'reject' | 'suspend' | 'reactivate') => void
}) {
  switch (status) {
    case 'pending':
      return (
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onAction('approve')}
            disabled={disabled}
            className="px-3 py-1 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            Approve
          </button>
          <button
            onClick={() => onAction('reject')}
            disabled={disabled}
            className="px-3 py-1 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-50"
          >
            Reject
          </button>
        </div>
      )

    case 'active':
      return (
        <button
          onClick={() => onAction('suspend')}
          disabled={disabled}
          className="px-3 py-1 text-sm font-medium text-yellow-700 bg-yellow-50 rounded-md hover:bg-yellow-100 disabled:opacity-50"
        >
          Suspend
        </button>
      )

    case 'suspended':
      return (
        <button
          onClick={() => onAction('reactivate')}
          disabled={disabled}
          className="px-3 py-1 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 disabled:opacity-50"
        >
          Reactivate
        </button>
      )

    default:
      return <span className="text-sm text-gray-400">-</span>
  }
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// =============================================================================
// ICONS
// =============================================================================

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  )
}

function UserGroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  )
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
      />
    </svg>
  )
}

function CogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  )
}

function ModulesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
      />
    </svg>
  )
}

function PimIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  )
}
